/**
 * Booking HTTP helpers for Flight Recorder (v3/booking only).
 */

import { isFlightRecorderEnabled, recordFlightEvent } from './flightRecorder'
import { redactValue } from './flightRedact'
import { getCurrentFlowSpanId, newRequestId } from './flightCorrelation'

const BOOKING_PATH_RE = /(?:^|\/)v3\/booking(?:\/|$)/i

/**
 * Strip query string and host; return path-ish string.
 * @param {string} url
 * @returns {string}
 */
export function normalizeUrlPath (url) {
  const raw = String(url || '')
  try {
    if (/^https?:\/\//i.test(raw)) {
      const u = new URL(raw)
      return u.pathname || raw
    }
  } catch {
    // fall through
  }
  const noQuery = raw.split('?')[0]
  return noQuery || raw
}

/**
 * @param {string} urlOrPath
 * @returns {boolean}
 */
export function isBookingHttpPath (urlOrPath) {
  const path = normalizeUrlPath(urlOrPath)
  return BOOKING_PATH_RE.test(path)
}

/**
 * @param {string} urlOrPath
 * @returns {string|null}
 */
export function extractBookingSessionId (urlOrPath) {
  const path = normalizeUrlPath(urlOrPath)
  const m = path.match(/v3\/booking\/([^/]+)\/(seats|cart|can-pay|confirm|block|unblock|tickets)/i)
  if (m && m[1] && m[1].toLowerCase() !== 'trips' && m[1].toLowerCase() !== 'fares') {
    return m[1]
  }
  return null
}

/**
 * Classify booking endpoint for timeline labels.
 * @param {string} urlOrPath
 * @param {string} [method]
 * @returns {{ kind: string, label: string }}
 */
export function classifyBookingHttp (urlOrPath, method = 'GET') {
  const path = normalizeUrlPath(urlOrPath).toLowerCase()
  const m = String(method || 'GET').toUpperCase()

  if (/\/block(?:\/|$|\?)/.test(path) || /\/seats\/[^/]+\/block/.test(path) || path.endsWith('/block')) {
    return { kind: 'block', label: 'BLOCK' }
  }
  if (/\/unblock(?:\/|$|\?)/.test(path) || path.endsWith('/unblock')) {
    return { kind: 'unblock', label: 'UNBLOCK' }
  }
  if (/\/v3\/booking\/trips(?:\/|$)/.test(path) || path.endsWith('/booking/trips')) {
    return { kind: 'trips', label: 'TRIPS' }
  }
  if (/\/seats\/live/.test(path)) {
    return { kind: 'seats_live', label: 'SEATS_LIVE' }
  }
  if (/\/seats(?:\/|$|\?)/.test(path)) {
    return { kind: 'seats', label: 'SEAT_MAP' }
  }
  if (/\/cart(?:\/|$|\?)/.test(path)) {
    return { kind: 'cart', label: 'CART' }
  }
  if (/\/can-pay(?:\/|$|\?)/.test(path)) {
    return { kind: 'can_pay', label: 'CAN_PAY' }
  }
  if (/\/confirm(?:\/|$|\?)/.test(path)) {
    return { kind: 'confirm', label: 'CONFIRM' }
  }
  if (/\/fares(?:\/|$|\?)/.test(path)) {
    return { kind: 'fares', label: 'FARES' }
  }
  return { kind: 'other', label: `${m}` }
}

/**
 * Pull useful ids from request/response bodies without dumping seatmaps.
 * @param {unknown} body
 * @returns {object}
 */
export function extractBookingIds (body) {
  if (!body || typeof body !== 'object') return {}
  const out = {}
  const pick = (obj, keys) => {
    if (!obj || typeof obj !== 'object') return
    for (const k of keys) {
      if (obj[k] != null && obj[k] !== '') out[k] = obj[k]
    }
  }
  const idKeys = ['session_id', 'sessionId', 'seatId', 'fareId', 'syncEntryId', 'cartId', 'cart_id', 'id']
  pick(body, idKeys)
  if (body.data && typeof body.data === 'object') {
    pick(body.data, idKeys)
    if (body.data.sync_entry_id && !out.syncEntryId) out.syncEntryId = body.data.sync_entry_id
    if (body.data.data && typeof body.data.data === 'object') {
      pick(body.data.data, idKeys)
      if (body.data.data.sync_entry_id && !out.syncEntryId) {
        out.syncEntryId = body.data.data.sync_entry_id
      }
    }
  }
  if (body.sync_entry_id && !out.syncEntryId) out.syncEntryId = body.sync_entry_id
  if (body.seat && typeof body.seat === 'object' && body.seat.seatId) {
    out.seatId = body.seat.seatId
  }
  if (body.seatId) out.seatId = body.seatId
  if (body.fareId !== undefined) out.fareId = body.fareId
  return out
}

function nowPerf () {
  return typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now()
}

/**
 * Wrap an axios call with HTTP:REQ/RES/ERR when recorder enabled and path is v3/booking.
 * Always returns the underlying promise result / rethrows.
 *
 * @param {object} opts
 * @param {'GET'|'POST'|string} opts.method
 * @param {string} opts.url - relative or absolute (pre-readUrl ok)
 * @param {string} [opts.fullUrl] - final URL if known
 * @param {unknown} [opts.body]
 * @param {() => Promise<any>} opts.run
 * @param {boolean} [opts.verboseBodies]
 */
export async function withFlightHttp (opts) {
  const { method, url, fullUrl, body, run } = opts
  const pathForCheck = fullUrl || url
  if (!isFlightRecorderEnabled() || !isBookingHttpPath(pathForCheck)) {
    return run()
  }

  const requestId = newRequestId()
  const flowSpanId = getCurrentFlowSpanId() || undefined
  const urlPath = normalizeUrlPath(pathForCheck)
  const sessionId = extractBookingSessionId(urlPath)
  const classified = classifyBookingHttp(urlPath, method)
  const reqIds = extractBookingIds(body)
  const t0 = nowPerf()
  const parentEventId = undefined

  recordFlightEvent(
    'HTTP:REQ',
    {
      method: String(method || 'GET').toUpperCase(),
      urlPath,
      kind: classified.kind,
      label: classified.label,
      requestId,
      flowSpanId,
      sessionId: sessionId || reqIds.sessionId || reqIds.session_id || undefined,
      seatId: reqIds.seatId,
      fareId: reqIds.fareId,
      syncEntryId: reqIds.syncEntryId,
      requestBody: redactValue(body, { bodyMode: 'keys' }),
    },
    'flightHttp.withFlightHttp',
  )

  try {
    const response = await run()
    const durationMs = Math.round((nowPerf() - t0) * 1000) / 1000
    const status = response?.status
    const data = response?.data
    const resIds = extractBookingIds(data)
    recordFlightEvent(
      'HTTP:RES',
      {
        method: String(method || 'GET').toUpperCase(),
        urlPath,
        kind: classified.kind,
        label: classified.label,
        requestId,
        parentEventId,
        flowSpanId,
        status,
        durationMs,
        sessionId: sessionId || resIds.sessionId || resIds.session_id || undefined,
        seatId: resIds.seatId || reqIds.seatId,
        fareId: resIds.fareId !== undefined ? resIds.fareId : reqIds.fareId,
        syncEntryId: resIds.syncEntryId,
        responseBody: redactValue(data, { bodyMode: 'keys' }),
      },
      'flightHttp.withFlightHttp',
    )
    return response
  } catch (error) {
    const durationMs = Math.round((nowPerf() - t0) * 1000) / 1000
    const status = error?.response?.status
    const code =
      error?.response?.data?.code ||
      error?.response?.data?.message?.code ||
      error?.code ||
      undefined
    recordFlightEvent(
      'HTTP:ERR',
      {
        method: String(method || 'GET').toUpperCase(),
        urlPath,
        kind: classified.kind,
        label: classified.label,
        requestId,
        flowSpanId,
        status,
        durationMs,
        code: code != null ? String(code) : undefined,
        sessionId: sessionId || reqIds.sessionId || undefined,
        seatId: reqIds.seatId,
        fareId: reqIds.fareId,
        message: error?.message ? String(error.message).slice(0, 200) : undefined,
      },
      'flightHttp.withFlightHttp',
    )
    throw error
  }
}
